import React, {useState, useEffect, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import {
  AdminMeeting,
  AccountRef,
  AdminUser,
  InternalNote,
  AccountOnboardingStatus,
  OnboardingNotes,
  WhatsAppMessage,
  WhatsAppTemplates,
  MeetingType,
  MeetingStatus,
  MEETING_TYPE_LABELS,
  MEETING_STATUS_LABELS,
  MEETING_TYPE_COLORS,
  MEETING_STATUS_COLORS,
} from './core/_models'
import {
  getMeetings,
  getMeeting,
  getAdminUsers,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  searchAccounts,
  addMeetingNote,
  rescheduleMeeting,
  getAccountOnboardingStatus,
  updateOnboardingNote,
  switchToAccount,
  sendMeetingWhatsApp,
  getWhatsAppTemplates,
  updateWhatsAppTemplates,
  getTakenSlots,
} from './core/_requests'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

// Why an account in the picker might not be a straightforward live customer.
// The picker deliberately includes lapsed subscriptions — you can't win a
// customer back without booking a call — so it has to say which ones they are.
const accountStatusBadges = (acc: any): Array<{label: string; color: string; bg: string}> => {
  if (!acc) return []
  const badges: Array<{label: string; color: string; bg: string}> = []
  if (acc.is_locked) badges.push({label: 'חסום', color: '#c2334a', bg: '#fff5f8'})
  if (acc.is_active === false) badges.push({label: 'לא פעיל', color: '#c2334a', bg: '#fff5f8'})
  if (acc.trial_period_end) {
    const daysAgo = Math.floor((Date.now() - new Date(acc.trial_period_end).getTime()) / 86400000)
    if (daysAgo >= 0) {
      badges.push({
        label: daysAgo === 0 ? 'המנוי פג היום' : `המנוי פג לפני ${daysAgo} ${daysAgo === 1 ? 'יום' : 'ימים'}`,
        color: '#b76e00',
        bg: '#fff8dd',
      })
    }
  }
  return badges
}

const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const getStatusStyle = (status: string) => {
  const styles: Record<string, {bg: string; color: string}> = {
    primary: {bg: 'rgba(54, 153, 255, 0.15)', color: '#3699ff'},
    success: {bg: 'rgba(27, 197, 189, 0.15)', color: '#1bc5bd'},
    warning: {bg: 'rgba(255, 168, 0, 0.15)', color: '#ffa800'},
    danger: {bg: 'rgba(246, 78, 96, 0.15)', color: '#f64e60'},
    info: {bg: 'rgba(54, 153, 255, 0.15)', color: '#3699ff'},
    secondary: {bg: 'rgba(126, 130, 153, 0.15)', color: '#7e8299'},
  }
  return styles[status] || styles.secondary
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: colors.bgPrimary,
  border: `1px solid ${colors.borderColor}`,
  borderRadius: '6px',
  color: colors.textPrimary,
  fontSize: '14px',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: colors.textMuted,
  fontSize: '11px',
  marginBottom: '6px',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
}

interface MeetingFormData {
  account: string
  assigned_to: string
  meeting_date: string
  meeting_time: string
  meeting_type: MeetingType
  title: string
  notes: string
  summary: string
  status: MeetingStatus
}

const emptyForm: MeetingFormData = {
  account: '',
  assigned_to: '',
  meeting_date: '',
  meeting_time: '',
  meeting_type: 'onboarding_new',
  title: '',
  notes: '',
  summary: '',
  status: 'scheduled',
}

const ONBOARDING_STEPS: {key: keyof OnboardingNotes; label: string; icon: string}[] = [
  {key: 'profiles', label: 'פרופילים', icon: 'bi-people'},
  {key: 'campaigns', label: 'קמפיינים', icon: 'bi-megaphone'},
  {key: 'groups', label: 'קבוצות', icon: 'bi-collection'},
  {key: 'posts', label: 'פוסטים', icon: 'bi-file-post'},
]

const OnboardingStepsRow: React.FC<{
  status: AccountOnboardingStatus
  accountId: string
  onNotesUpdated: (notes: OnboardingNotes) => void
}> = ({status, accountId, onNotesUpdated}) => {
  const [editingStep, setEditingStep] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const notes = status.onboarding_notes || {profiles: '', campaigns: '', groups: '', posts: ''}

  const getCounts = (key: string): number => {
    switch (key) {
      case 'profiles': return status.profiles.length + status.subProfiles.length
      case 'campaigns': return status.campaigns.length
      case 'groups': return status.total_groups || 0
      case 'posts': return status.total_posts || 0
      default: return 0
    }
  }

  const startEdit = (step: string) => {
    setEditingStep(step)
    setEditValue(notes[step as keyof OnboardingNotes] || '')
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const saveNote = async (step: string) => {
    setEditingStep(null)
    const oldNote = notes[step as keyof OnboardingNotes] || ''
    if (editValue === oldNote) return
    try {
      const res = await updateOnboardingNote(accountId, step, editValue)
      onNotesUpdated(res.data.onboarding_notes)
    } catch (e) {
      console.error('Failed to save note:', e)
    }
  }

  const cancelEdit = () => {
    setEditingStep(null)
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px',
        padding: '10px 24px 12px',
        background: 'rgba(0,0,0,0.02)',
        borderTop: `1px solid rgba(0,0,0,0.04)`,
        borderBottom: `1px solid rgba(0,0,0,0.05)`,
      }}
    >
      {ONBOARDING_STEPS.map(({key, label, icon}) => {
        const count = getCounts(key)
        const done = count > 0
        const note = notes[key] || ''
        const isEditing = editingStep === key

        return (
          <div
            key={key}
            style={{
              background: colors.bgSecondary,
              borderRadius: '8px',
              padding: '10px 12px',
              border: `1px solid ${done ? 'rgba(27, 197, 189, 0.25)' : colors.borderColor}`,
              position: 'relative',
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px'}}>
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background: done ? 'rgba(27, 197, 189, 0.12)' : 'rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <i className={`bi ${icon}`} style={{fontSize: '14px', color: done ? '#1bc5bd' : colors.textMuted}}></i>
                {done && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-3px',
                      right: '-3px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: '#1bc5bd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className='bi bi-check' style={{fontSize: '9px', color: '#fff'}}></i>
                  </div>
                )}
              </div>
              <span style={{fontSize: '12px', fontWeight: 600, color: colors.textPrimary}}>{label}</span>
              <span
                style={{
                  marginRight: 'auto',
                  padding: '1px 7px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  background: done ? 'rgba(27, 197, 189, 0.12)' : 'rgba(0,0,0,0.06)',
                  color: done ? '#1bc5bd' : colors.textMuted,
                }}
              >
                {count}
              </span>
            </div>

            {/* Note */}
            {isEditing ? (
              <input
                ref={inputRef}
                type='text'
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => saveNote(key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveNote(key)
                  if (e.key === 'Escape') cancelEdit()
                }}
                style={{
                  width: '100%',
                  padding: '4px 6px',
                  fontSize: '11px',
                  border: `1px solid ${colors.accentPrimary}`,
                  borderRadius: '4px',
                  outline: 'none',
                  color: colors.textPrimary,
                  background: colors.bgPrimary,
                }}
              />
            ) : (
              <div
                onClick={() => startEdit(key)}
                style={{
                  fontSize: '11px',
                  color: note ? colors.textSecondary : colors.textMuted,
                  fontStyle: note ? 'normal' : 'italic',
                  cursor: 'pointer',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  minHeight: '22px',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgPrimary)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {note || (count === 0 ? 'הוסף הערה...' : '')}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const MeetingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [meetings, setMeetings] = useState<AdminMeeting[]>([])
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)

  // Filters
  const statusFilter = searchParams.get('status') ?? 'scheduled' // default to scheduled, use 'all' to show everything
  const typeFilter = searchParams.get('type') || ''
  const assignedFilter = searchParams.get('assigned_to') || ''
  const searchQuery = searchParams.get('search') || ''
  const [localSearch, setLocalSearch] = useState(searchQuery)
  const [searchFocused, setSearchFocused] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalMeetings, setTotalMeetings] = useState(0)

  // Stats
  const [statsData, setStatsData] = useState({
    totalScheduled: 0,
    todayMeetings: 0,
    thisWeek: 0,
    completed: 0,
  })

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingMeeting, setEditingMeeting] = useState<AdminMeeting | null>(null)
  const [formData, setFormData] = useState<MeetingFormData>(emptyForm)
  const [formLoading, setFormLoading] = useState(false)

  // Account search
  const [accountSearch, setAccountSearch] = useState('')
  const [accountResults, setAccountResults] = useState<any[]>([])
  // Lapsed subscriptions are always searchable (booking a meeting is often the
  // point of a lapsed customer). This opt-in additionally reaches accounts that
  // were disabled or locked outright.
  const [accountSearchAll, setAccountSearchAll] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<any>(null)
  const [showAccountDropdown, setShowAccountDropdown] = useState(false)
  const accountSearchTimeout = useRef<any>(null)

  // Admin search
  const [adminSearch, setAdminSearch] = useState('')
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null)
  const [showAdminDropdown, setShowAdminDropdown] = useState(false)
  const adminSearchRef = useRef<HTMLDivElement>(null)
  const accountSearchRef = useRef<HTMLDivElement>(null)

  // Internal notes
  const [newNoteText, setNewNoteText] = useState('')
  const [noteLoading, setNoteLoading] = useState(false)

  // Account onboarding statuses
  const [accountStatuses, setAccountStatuses] = useState<Record<string, AccountOnboardingStatus>>({})

  // Reschedule modal
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [rescheduleTarget, setRescheduleTarget] = useState<AdminMeeting | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [rescheduleTime, setRescheduleTime] = useState('')
  const [rescheduleLoading, setRescheduleLoading] = useState(false)

  // Time slots (9:00-17:00 every 20 min)
  const TIME_SLOTS = Array.from({length: 25}, (_, i) => {
    const h = Math.floor(i * 20 / 60) + 9
    const m = (i * 20) % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  })
  interface TakenSlot { time: string; minutes: number; title: string; account: string }
  const [takenSlots, setTakenSlots] = useState<TakenSlot[]>([])
  const [rescheduleTakenSlots, setRescheduleTakenSlots] = useState<TakenSlot[]>([])

  const isSlotTaken = (slot: string, taken: TakenSlot[]) => {
    const [h, m] = slot.split(':').map(Number)
    const slotMin = h * 60 + m
    return taken.find(t => Math.abs(t.minutes - slotMin) < 20)
  }

  const isSlotPast = (slot: string, date: string) => {
    const now = moment()
    const slotMoment = moment(`${date}T${slot}`, 'YYYY-MM-DDTHH:mm')
    return slotMoment.isBefore(now)
  }

  // Fetch taken slots when admin + date change in create/edit form
  useEffect(() => {
    if (!formData.assigned_to || !formData.meeting_date) { setTakenSlots([]); return }
    getTakenSlots(formData.assigned_to, formData.meeting_date, editingMeeting?._id)
      .then(res => setTakenSlots(res.data.takenSlots))
      .catch(() => setTakenSlots([]))
  }, [formData.assigned_to, formData.meeting_date, editingMeeting?._id])

  // Fetch taken slots for reschedule modal
  useEffect(() => {
    if (!rescheduleTarget || !rescheduleDate) { setRescheduleTakenSlots([]); return }
    const adminId = typeof rescheduleTarget.assigned_to === 'string' ? rescheduleTarget.assigned_to : rescheduleTarget.assigned_to._id
    getTakenSlots(adminId, rescheduleDate, rescheduleTarget._id)
      .then(res => setRescheduleTakenSlots(res.data.takenSlots))
      .catch(() => setRescheduleTakenSlots([]))
  }, [rescheduleTarget, rescheduleDate])

  // Quick note (inline)
  const [quickNoteMeetingId, setQuickNoteMeetingId] = useState<string | null>(null)
  const [quickNoteText, setQuickNoteText] = useState('')
  const [quickNoteLoading, setQuickNoteLoading] = useState(false)
  const quickNoteRef = useRef<HTMLInputElement>(null)

  // WhatsApp
  const [whatsappSending, setWhatsappSending] = useState<string | null>(null) // 'scheduled' | 'reminder' | null
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [templates, setTemplates] = useState<WhatsAppTemplates>({scheduled: '', reminder: ''})
  const [templateLoading, setTemplateLoading] = useState(false)


  useEffect(() => {
    loadAdmins()
    // Auto-open create modal if ?create=true
    if (searchParams.get('create') === 'true') {
      openCreateModal()
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('create')
      setSearchParams(newParams, {replace: true})
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [statusFilter, typeFilter, assignedFilter, searchQuery, currentPage])

  // Fetch account onboarding statuses when meetings change
  useEffect(() => {
    if (meetings.length === 0) return
    const accountIds = Array.from(new Set(
      meetings
        .map((m) => typeof m.account === 'string' ? m.account : (m.account as AccountRef)?._id)
        .filter(Boolean) as string[]
    ))
    // Only fetch accounts we don't already have
    const toFetch = accountIds.filter((id) => !accountStatuses[id])
    if (toFetch.length === 0) return
    Promise.all(
      toFetch.map((id) =>
        getAccountOnboardingStatus(id)
          .then((res) => ({ id, data: res.data }))
          .catch(() => null)
      )
    ).then((results) => {
      const newStatuses: Record<string, AccountOnboardingStatus> = {}
      results.forEach((r) => {
        if (r) newStatuses[r.id] = r.data
      })
      if (Object.keys(newStatuses).length > 0) {
        setAccountStatuses((prev) => ({ ...prev, ...newStatuses }))
      }
    })
  }, [meetings])

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountSearchRef.current && !accountSearchRef.current.contains(e.target as Node)) {
        setShowAccountDropdown(false)
      }
      if (adminSearchRef.current && !adminSearchRef.current.contains(e.target as Node)) {
        setShowAdminDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadAdmins = async () => {
    try {
      const res = await getAdminUsers()
      setAdmins(res.data.admins)
    } catch (e) {
      console.error('Failed to load admins:', e)
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: currentPage,
        limit: 25,
        sort: 'meeting_date',
      }
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter
      if (typeFilter) params.meeting_type = typeFilter
      if (assignedFilter) params.assigned_to = assignedFilter
      if (searchQuery) params.search = searchQuery

      const res = await getMeetings(params)
      setMeetings(res.data.meetings)
      setTotalPages(res.data.pagination.pages)
      setTotalMeetings(res.data.pagination.total)

      // Calculate stats from a separate broader query
      const allScheduled = await getMeetings({status: 'scheduled', limit: 1})
      const today = moment().format('YYYY-MM-DD')
      const todayRes = await getMeetings({
        status: 'scheduled',
        date_from: today,
        date_to: today,
        limit: 1,
      })
      const weekEnd = moment().endOf('week').format('YYYY-MM-DD')
      const weekRes = await getMeetings({
        status: 'scheduled',
        date_from: today,
        date_to: weekEnd,
        limit: 1,
      })
      const completedRes = await getMeetings({status: 'completed', limit: 1})

      setStatsData({
        totalScheduled: allScheduled.data.pagination.total,
        todayMeetings: todayRes.data.pagination.total,
        thisWeek: weekRes.data.pagination.total,
        completed: completedRes.data.pagination.total,
      })
    } catch (error) {
      console.error('Failed to load meetings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const newParams = new URLSearchParams(searchParams)
    if (localSearch) {
      newParams.set('search', localSearch)
    } else {
      newParams.delete('search')
    }
    setSearchParams(newParams)
    setCurrentPage(1)
  }

  const setFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    setSearchParams(newParams)
    setCurrentPage(1)
  }

  const loadRecentAccounts = async (searchAll = accountSearchAll) => {
    try {
      const res = await searchAccounts('', {searchAll})
      setAccountResults(res.data.accounts || [])
      setShowAccountDropdown(true)
    } catch (e) {
      console.error('Account load error:', e)
    }
  }

  const runAccountSearch = async (val: string, searchAll = accountSearchAll) => {
    try {
      const res = await searchAccounts(val, {searchAll})
      setAccountResults(res.data.accounts || [])
      setShowAccountDropdown(true)
    } catch (e) {
      console.error('Account search error:', e)
    }
  }

  const handleAccountSearch = (val: string) => {
    setAccountSearch(val)
    if (accountSearchTimeout.current) clearTimeout(accountSearchTimeout.current)
    if (val.length < 2) {
      // Show recent accounts when search is cleared
      loadRecentAccounts()
      return
    }
    accountSearchTimeout.current = setTimeout(() => runAccountSearch(val), 300)
  }

  // Re-run whatever is currently in the box against the new scope.
  const toggleAccountSearchAll = (next: boolean) => {
    setAccountSearchAll(next)
    if (accountSearchTimeout.current) clearTimeout(accountSearchTimeout.current)
    if (accountSearch.trim().length >= 2) runAccountSearch(accountSearch, next)
    else loadRecentAccounts(next)
  }

  const selectAccount = (acc: any) => {
    setSelectedAccount(acc)
    setFormData({...formData, account: acc._id})
    setAccountSearch('')
    setShowAccountDropdown(false)
  }

  const openCreateModal = () => {
    setEditingMeeting(null)
    setFormData(emptyForm)
    setSelectedAccount(null)
    setAccountSearch('')
    setSelectedAdmin(null)
    setAdminSearch('')
    setShowModal(true)
  }

  const openEditModal = async (meeting: AdminMeeting) => {
    // Fetch fresh data with populated notes
    try {
      const res = await getMeeting(meeting._id)
      const fresh = res.data.meeting
      setEditingMeeting(fresh)
      const acc = fresh.account as AccountRef
      setSelectedAccount(acc)
      setAccountSearch(acc ? `${acc.first_name || ''} ${acc.last_name || ''} (${acc.email})` : '')
      const admin = fresh.assigned_to as AdminUser
      setSelectedAdmin(typeof fresh.assigned_to === 'string' ? null : admin)
      setAdminSearch(admin && typeof admin !== 'string' ? admin.username : '')
      const mDate = moment(fresh.meeting_date)
      setFormData({
        // A lead meeting (affiliate-booked, no customer account yet) has none —
        // the admin can attach one here to turn it into a customer meeting.
        account: typeof fresh.account === 'string' ? fresh.account : fresh.account?._id || '',
        assigned_to: typeof fresh.assigned_to === 'string' ? fresh.assigned_to : fresh.assigned_to._id,
        meeting_date: mDate.format('YYYY-MM-DD'),
        meeting_time: mDate.format('HH:mm'),
        meeting_type: fresh.meeting_type,
        title: fresh.title,
        notes: fresh.notes || '',
        summary: fresh.summary || '',
        status: fresh.status,
      })
      setNewNoteText('')
      setShowModal(true)
    } catch (e) {
      console.error('Failed to fetch meeting:', e)
    }
  }

  const selectAdmin = (admin: AdminUser) => {
    setSelectedAdmin(admin)
    setFormData({...formData, assigned_to: admin._id})
    setAdminSearch(admin.username)
    setShowAdminDropdown(false)
  }

  const filteredAdmins = admins.filter(
    (a) =>
      !adminSearch ||
      a.username.toLowerCase().includes(adminSearch.toLowerCase()) ||
      a.email.toLowerCase().includes(adminSearch.toLowerCase())
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.account || !formData.assigned_to || !formData.meeting_date || !formData.title) {
      alert('נא למלא את כל השדות הנדרשים')
      return
    }

    setFormLoading(true)
    try {
      const meetingDate = `${formData.meeting_date}T${formData.meeting_time || '09:00'}`

      if (editingMeeting) {
        await updateMeeting(editingMeeting._id, {
          account: formData.account,
          assigned_to: formData.assigned_to,
          meeting_date: meetingDate,
          meeting_type: formData.meeting_type,
          title: formData.title,
          notes: formData.notes,
          summary: formData.summary,
          status: formData.status,
        })
      } else {
        await createMeeting({
          account: formData.account,
          assigned_to: formData.assigned_to,
          meeting_date: meetingDate,
          meeting_type: formData.meeting_type,
          title: formData.title,
          notes: formData.notes,
        })
      }
      setShowModal(false)
      loadData()
    } catch (error: any) {
      console.error('Failed to save meeting:', error)
      alert(error.response?.data?.error || 'שגיאה בשמירת הפגישה')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('האם למחוק את הפגישה?')) return
    try {
      await deleteMeeting(id)
      loadData()
    } catch (error) {
      console.error('Failed to delete meeting:', error)
    }
  }

  const handleStatusChange = async (meeting: AdminMeeting, newStatus: MeetingStatus) => {
    try {
      await updateMeeting(meeting._id, {status: newStatus})
      loadData()
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const handleLoginAsAccount = async (meeting: AdminMeeting) => {
    const accountId = typeof meeting.account === 'string' ? meeting.account : (meeting.account as AccountRef)?._id
    if (!accountId) return
    try {
      const res = await switchToAccount(accountId)
      window.open(`${window.location.origin}/?overide-token=${res.data.token}`, '_blank')
    } catch (err) {
      console.error('Failed to switch account:', err)
    }
  }

  const handleAddNote = async () => {
    if (!editingMeeting || !newNoteText.trim()) return
    setNoteLoading(true)
    try {
      const res = await addMeetingNote(editingMeeting._id, newNoteText.trim())
      setEditingMeeting(res.data.meeting)
      setNewNoteText('')
    } catch (error) {
      console.error('Failed to add note:', error)
    } finally {
      setNoteLoading(false)
    }
  }

  const handleSendWhatsApp = async (meeting: AdminMeeting, type: 'scheduled' | 'reminder') => {
    setWhatsappSending(type)
    try {
      const res = await sendMeetingWhatsApp(meeting._id, type)
      setEditingMeeting(res.data.meeting)
      loadData()
    } catch (error: any) {
      console.error('Failed to send WhatsApp:', error)
      alert(error.response?.data?.error || 'שגיאה בשליחת הודעת WhatsApp')
    } finally {
      setWhatsappSending(null)
    }
  }

  const handleSendReminder = async (meeting: AdminMeeting) => {
    await handleSendWhatsApp(meeting, 'reminder')
  }

  const handleQuickNote = async (meetingId: string) => {
    if (!quickNoteText.trim()) {
      setQuickNoteMeetingId(null)
      return
    }
    setQuickNoteLoading(true)
    try {
      await addMeetingNote(meetingId, quickNoteText.trim())
      setQuickNoteText('')
      setQuickNoteMeetingId(null)
      loadData()
    } catch (e) {
      console.error('Failed to add quick note:', e)
    } finally {
      setQuickNoteLoading(false)
    }
  }

  const openQuickNote = (meetingId: string) => {
    setQuickNoteMeetingId(meetingId)
    setQuickNoteText('')
    setTimeout(() => quickNoteRef.current?.focus(), 50)
  }

  const loadTemplates = async () => {
    try {
      const res = await getWhatsAppTemplates()
      setTemplates(res.data.templates)
    } catch (e) {
      console.error('Failed to load templates:', e)
    }
  }

  const handleSaveTemplates = async () => {
    setTemplateLoading(true)
    try {
      const res = await updateWhatsAppTemplates(templates)
      setTemplates(res.data.templates)
      setShowTemplateModal(false)
    } catch (e) {
      console.error('Failed to save templates:', e)
    } finally {
      setTemplateLoading(false)
    }
  }

  const openRescheduleModal = (meeting: AdminMeeting) => {
    setRescheduleTarget(meeting)
    const mDate = moment(meeting.meeting_date)
    setRescheduleDate(mDate.format('YYYY-MM-DD'))
    setRescheduleTime(mDate.format('HH:mm'))
    setShowRescheduleModal(true)
  }

  const handleReschedule = async () => {
    if (!rescheduleTarget || !rescheduleDate) return
    setRescheduleLoading(true)
    try {
      const dateTime = `${rescheduleDate}T${rescheduleTime || '09:00'}`
      await rescheduleMeeting(rescheduleTarget._id, dateTime)
      setShowRescheduleModal(false)
      setRescheduleTarget(null)
      loadData()
    } catch (error: any) {
      console.error('Failed to reschedule:', error)
      alert(error.response?.data?.error || 'שגיאה בדחיית הפגישה')
    } finally {
      setRescheduleLoading(false)
    }
  }

  const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

  const getProfilePicture = (profile: {facebook_id?: string; profile_picture_url?: string}) => {
    if (profile.profile_picture_url) return profile.profile_picture_url
    if (profile.facebook_id) return `https://graph.facebook.com/${profile.facebook_id}/picture?type=large&access_token=${FB_ACCESS_TOKEN}`
    return ''
  }

  const getAccountName = (meeting: AdminMeeting) => {
    const acc = meeting.account as AccountRef
    // A meeting an affiliate booked for a LEAD has no account yet — it carries
    // the contact details instead.
    if (!acc || typeof acc === 'string') return meeting.contact_name || 'Unknown'
    return `${acc.first_name || ''} ${acc.last_name || ''}`.trim() || acc.email
  }

  const getAdminName = (meeting: AdminMeeting) => {
    const admin = meeting.assigned_to as AdminUser
    if (!admin || typeof admin === 'string') return '—'
    return admin.username
  }

  const stats = [
    {
      label: 'מתוזמנות',
      value: statsData.totalScheduled,
      icon: 'bi-calendar-check',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'היום',
      value: statsData.todayMeetings,
      icon: 'bi-calendar-day',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      label: 'השבוע',
      value: statsData.thisWeek,
      icon: 'bi-calendar-week',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      label: 'הושלמו',
      value: statsData.completed,
      icon: 'bi-check-circle-fill',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
  ]

  return (
    <div style={{padding: 0}}>
      {/* Page Header */}
      <div style={{marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div>
          <h1 style={{fontSize: '24px', fontWeight: 600, color: colors.textPrimary, margin: 0}}>
            ניהול פגישות
          </h1>
          <p style={{color: colors.textSecondary, marginTop: '8px', fontSize: '14px'}}>
            תזמון וניהול פגישות אונבורדינג ומעקב
          </p>
        </div>
        <button
          onClick={openCreateModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 20px',
            background: colors.accentPrimary,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <i className='bi bi-plus-lg' style={{fontSize: '16px'}}></i>
          פגישה חדשה
        </button>
      </div>

      {/* Stats Grid */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px'}}>
        {stats.map((stat, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '20px',
              cursor: 'default',
              transition: 'all 0.2s ease',
              transform: hoveredCard === i ? 'translateY(-2px)' : 'none',
              border: `1px solid ${hoveredCard === i ? colors.borderColor : 'transparent'}`,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}
            >
              <i className={`bi ${stat.icon}`} style={{fontSize: '20px', color: '#fff'}}></i>
            </div>
            <div style={{fontSize: '28px', fontWeight: 700, color: colors.textPrimary, marginBottom: '4px'}}>
              {stat.value}
            </div>
            <div style={{fontSize: '13px', color: colors.textSecondary}}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '20px',
          alignItems: 'center',
          background: colors.bgTertiary,
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <form onSubmit={handleSearch} style={{flex: 1, position: 'relative'}}>
          <i
            className='bi bi-search'
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.textMuted,
              fontSize: '16px',
            }}
          ></i>
          <input
            type='text'
            placeholder='חיפוש לפי שם חשבון, כותרת...'
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              width: '100%',
              padding: '12px 44px 12px 16px',
              background: searchFocused ? colors.bgSecondary : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: colors.textPrimary,
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s ease',
            }}
          />
        </form>

        <div style={{display: 'flex', gap: '12px'}}>
          <select
            value={statusFilter}
            onChange={(e) => setFilter('status', e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              border: `1px solid ${colors.borderColor}`,
              background: colors.bgSecondary,
              color: colors.textPrimary,
              fontSize: '13px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '130px',
            }}
          >
            <option value='all'>כל הסטטוסים</option>
            <option value='scheduled'>מתוזמן</option>
            <option value='completed'>הושלם</option>
            <option value='cancelled'>בוטל</option>
            <option value='no_show'>לא הגיע</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setFilter('type', e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              border: `1px solid ${colors.borderColor}`,
              background: colors.bgSecondary,
              color: colors.textPrimary,
              fontSize: '13px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '130px',
            }}
          >
            <option value=''>כל הסוגים</option>
            <option value='onboarding_new'>אונבורדינג לקוח חדש</option>
            <option value='onboarding'>אונבורדינג</option>
            <option value='followup'>מעקב</option>
            <option value='technical'>טכני</option>
            <option value='sales'>מכירות</option>
            <option value='other'>אחר</option>
          </select>

          <select
            value={assignedFilter}
            onChange={(e) => setFilter('assigned_to', e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              border: `1px solid ${colors.borderColor}`,
              background: colors.bgSecondary,
              color: colors.textPrimary,
              fontSize: '13px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '130px',
            }}
          >
            <option value=''>כל האדמינים</option>
            {admins.map((a) => (
              <option key={a._id} value={a._id}>
                {a.username}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{background: colors.bgTertiary, borderRadius: '12px', overflow: 'hidden'}}>
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 2fr 1fr 1fr 1fr 1fr 220px',
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          {['תאריך/שעה', 'חשבון', 'סוג', 'אדמין', 'סטטוס', 'כותרת', 'פעולות'].map((header, i) => (
            <div
              key={i}
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.5px',
                color: colors.textMuted,
              }}
            >
              {header}
            </div>
          ))}
        </div>

        {/* Table Body */}
        {loading ? (
          <div style={{padding: '60px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${colors.bgHover}`,
                borderTopColor: colors.accentPrimary,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            ></div>
          </div>
        ) : meetings.length === 0 ? (
          <div style={{padding: '60px', textAlign: 'center'}}>
            <i className='bi bi-calendar-x' style={{fontSize: '48px', color: colors.textMuted, marginBottom: '16px', display: 'block'}}></i>
            <div style={{fontSize: '18px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px'}}>לא נמצאו פגישות</div>
            <div style={{fontSize: '14px', color: colors.textSecondary}}>צור פגישה חדשה או שנה את הפילטרים</div>
          </div>
        ) : (
          <>
            {(() => {
              // Find the next upcoming meeting for countdown
              const now = moment()
              const nextUpcoming = meetings.find(m => m.status === 'scheduled' && moment(m.meeting_date).isAfter(now))
              const nextMeetingId = nextUpcoming?._id

              return meetings.map((meeting) => {
              const typeColor = MEETING_TYPE_COLORS[meeting.meeting_type]
              const statusColor = MEETING_STATUS_COLORS[meeting.status]
              const typeStyle = getStatusStyle(typeColor)
              const sstyle = getStatusStyle(statusColor)
              const meetingMoment = moment(meeting.meeting_date)
              const isToday = meetingMoment.isSame(moment(), 'day')
              const isPast = meetingMoment.isBefore(moment())
              const isPastScheduled = isPast && meeting.status === 'scheduled'
              const isCompleted = meeting.status === 'completed'
              const isCancelled = meeting.status === 'cancelled' || meeting.status === 'no_show'
              const isNext = meeting._id === nextMeetingId
              const accountId = typeof meeting.account === 'string' ? meeting.account : (meeting.account as AccountRef)?._id
              const acc = meeting.account as AccountRef
              const phone = (typeof acc !== 'string' ? acc?.phone : undefined) || meeting.contact_phone
              const status = accountId ? accountStatuses[accountId] : undefined

              // WhatsApp/reminder labels
              const whatsappMessages = meeting.whatsapp_messages || []
              const hasSentWhatsapp = whatsappMessages.some(m => m.type === 'scheduled' && m.status === 'sent')
              const hasSentReminder = whatsappMessages.some(m => m.type === 'reminder' && m.status === 'sent')

              // Time until meeting
              const diffMs = meetingMoment.diff(moment())
              const diffMins = Math.floor(diffMs / 60000)
              const diffHours = Math.floor(diffMins / 60)
              const remainingMins = diffMins % 60

              let timeUntil = ''
              if (isNext && !isPast) {
                if (diffMins < 1) timeUntil = 'עכשיו!'
                else if (diffMins < 60) timeUntil = `בעוד ${diffMins} דק׳`
                else if (diffHours < 24) timeUntil = `בעוד ${diffHours}:${remainingMins.toString().padStart(2, '0')}`
                else timeUntil = `בעוד ${Math.floor(diffHours / 24)} ימים`
              }

              // Row background color
              let rowBg = 'transparent'
              if (hoveredRow === meeting._id) rowBg = colors.bgHover
              else if (isPastScheduled) rowBg = 'rgba(246, 78, 96, 0.06)'
              else if (isNext) rowBg = 'rgba(54, 153, 255, 0.06)'
              else if (isCompleted) rowBg = 'rgba(27, 197, 189, 0.03)'
              else if (isCancelled) rowBg = 'rgba(0, 0, 0, 0.02)'

              return (
                <React.Fragment key={meeting._id}>
                <div
                  onMouseEnter={() => setHoveredRow(meeting._id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 2fr 1fr 1fr 1fr 1fr 220px',
                    padding: '14px 24px',
                    alignItems: 'center',
                    borderBottom: status ? 'none' : `1px solid rgba(0,0,0,0.05)`,
                    borderRight: isNext ? '3px solid #3699ff' : isPastScheduled ? '3px solid #f64e60' : '3px solid transparent',
                    background: rowBg,
                    transition: 'background 0.15s ease',
                    opacity: isCancelled ? 0.6 : 1,
                  }}
                >
                  {/* Date/Time */}
                  <div>
                    <div style={{fontWeight: 600, color: isToday ? colors.accentPrimary : isPastScheduled ? colors.accentDanger : colors.textPrimary, fontSize: '14px'}}>
                      {meetingMoment.format('DD/MM/YYYY')}
                      {isToday && <span style={{marginRight: '6px', fontSize: '11px', color: colors.accentPrimary, fontWeight: 700}}>(היום)</span>}
                    </div>
                    <div style={{fontSize: '13px', color: isPastScheduled ? colors.accentDanger : colors.textSecondary, marginTop: '2px', fontWeight: 600}}>
                      {meetingMoment.format('HH:mm')}
                    </div>
                    {timeUntil && (
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: diffMins < 15 ? colors.accentDanger : diffMins < 60 ? colors.accentWarning : colors.accentPrimary,
                        marginTop: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}>
                        <i className='bi bi-clock' style={{fontSize: '10px'}}></i>
                        {timeUntil}
                      </div>
                    )}
                  </div>

                  {/* Account + Phone */}
                  <div>
                    <div style={{fontWeight: 500, color: colors.textPrimary, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                      {getAccountName(meeting)}
                      {!accountId && (
                        <span
                          title='ליד של שותף — עדיין אין חשבון לקוח'
                          style={{padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 600, background: 'rgba(255, 168, 0, 0.12)', color: '#ffa800'}}
                        >
                          ליד
                        </span>
                      )}
                      {phone && (
                        <a
                          href={`tel:${phone}`}
                          title={`התקשר ${phone}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            background: 'rgba(27, 197, 189, 0.12)',
                            color: '#1bc5bd',
                            fontSize: '12px',
                            textDecoration: 'none',
                            flexShrink: 0,
                          }}
                        >
                          <i className='bi bi-telephone-fill'></i>
                        </a>
                      )}
                    </div>
                    {phone && <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '1px'}}>{phone}</div>}
                    {/* WhatsApp & Reminder labels */}
                    <div style={{display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap'}}>
                      {hasSentWhatsapp && (
                        <span style={{
                          padding: '1px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: 600,
                          background: 'rgba(37, 211, 102, 0.12)',
                          color: '#25D366',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}>
                          <i className='bi bi-whatsapp' style={{fontSize: '9px'}}></i>
                          נשלח
                        </span>
                      )}
                      {hasSentReminder && (
                        <span style={{
                          padding: '1px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: 600,
                          background: 'rgba(255, 168, 0, 0.12)',
                          color: '#ffa800',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}>
                          <i className='bi bi-bell-fill' style={{fontSize: '9px'}}></i>
                          תזכורת
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: typeStyle.bg,
                        color: typeStyle.color,
                      }}
                    >
                      {MEETING_TYPE_LABELS[meeting.meeting_type]}
                    </span>
                  </div>

                  {/* Admin */}
                  <div style={{color: colors.textSecondary, fontSize: '13px'}}>{getAdminName(meeting)}</div>

                  {/* Status */}
                  <div>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: sstyle.bg,
                        color: sstyle.color,
                      }}
                    >
                      {MEETING_STATUS_LABELS[meeting.status]}
                    </span>
                    {isPastScheduled && (
                      <div style={{fontSize: '10px', color: colors.accentDanger, fontWeight: 600, marginTop: '3px'}}>
                        <i className='bi bi-exclamation-triangle' style={{fontSize: '9px', marginLeft: '2px'}}></i>
                        עבר
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div
                    style={{
                      color: colors.textSecondary,
                      fontSize: '13px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' as const,
                    }}
                    title={meeting.title}
                  >
                    {meeting.title}
                    {meeting.source === 'affiliate' && (() => {
                      // Booked from the affiliate portal — show who, so whoever
                      // takes the meeting knows who to call back.
                      const aff = typeof meeting.created_by_affiliate === 'string' ? null : meeting.created_by_affiliate
                      const affName = aff
                        ? `${aff.first_name || ''} ${aff.last_name || ''}`.trim() || aff.company_name || ''
                        : ''
                      return (
                        <div
                          style={{fontSize: '10px', color: colors.accentPrimary, fontWeight: 600, marginTop: '2px'}}
                          title={`נקבע ע"י שותף${affName ? ': ' + affName : ''}${aff && aff.phone ? ' · ' + aff.phone : ''}`}
                        >
                          <i className='bi bi-people' style={{fontSize: '9px', marginLeft: '3px'}}></i>
                          שותף{affName ? ` · ${affName}` : ''}
                        </div>
                      )
                    })()}
                  </div>

                  {/* Actions */}
                  <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
                    <button
                      onClick={() => handleLoginAsAccount(meeting)}
                      title='התחבר כחשבון'
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'rgba(102, 126, 234, 0.1)',
                        color: '#667eea',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                      }}
                    >
                      <i className='bi bi-box-arrow-in-right'></i>
                    </button>
                    <button
                      onClick={() => openEditModal(meeting)}
                      title='ערוך'
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'rgba(54, 153, 255, 0.1)',
                        color: colors.accentPrimary,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                      }}
                    >
                      <i className='bi bi-pencil'></i>
                    </button>

                    <button
                      onClick={() => openQuickNote(meeting._id)}
                      title='הוסף הערה מהירה'
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'rgba(255, 168, 0, 0.1)',
                        color: colors.accentWarning,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                      }}
                    >
                      <i className='bi bi-chat-dots'></i>
                    </button>

                    {meeting.status === 'scheduled' && (
                      <button
                        onClick={() => handleSendReminder(meeting)}
                        title='שלח תזכורת WhatsApp'
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '6px',
                          border: 'none',
                          background: 'rgba(37, 211, 102, 0.1)',
                          color: '#25D366',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                        }}
                      >
                        <i className='bi bi-whatsapp'></i>
                      </button>
                    )}

                    {meeting.status === 'scheduled' && (
                      <button
                        onClick={() => openRescheduleModal(meeting)}
                        title='דחה פגישה'
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '6px',
                          border: 'none',
                          background: 'rgba(255, 168, 0, 0.1)',
                          color: colors.accentWarning,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                        }}
                      >
                        <i className='bi bi-calendar-plus'></i>
                      </button>
                    )}

                    {meeting.status === 'scheduled' && (
                      <button
                        onClick={() => handleStatusChange(meeting, 'completed')}
                        title='סמן כהושלם'
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '6px',
                          border: 'none',
                          background: 'rgba(27, 197, 189, 0.1)',
                          color: colors.accentSuccess,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                        }}
                      >
                        <i className='bi bi-check-lg'></i>
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(meeting._id)}
                      title='מחק'
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'rgba(246, 78, 96, 0.1)',
                        color: colors.accentDanger,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                      }}
                    >
                      <i className='bi bi-trash'></i>
                    </button>
                  </div>
                </div>

                {/* Quick Note Input */}
                {quickNoteMeetingId === meeting._id && (
                  <div style={{
                    padding: '8px 24px',
                    background: 'rgba(255, 168, 0, 0.04)',
                    borderBottom: `1px solid rgba(0,0,0,0.05)`,
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                  }}>
                    <i className='bi bi-chat-dots' style={{color: colors.accentWarning, fontSize: '14px'}}></i>
                    <input
                      ref={quickNoteRef}
                      type='text'
                      value={quickNoteText}
                      onChange={(e) => setQuickNoteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleQuickNote(meeting._id)
                        if (e.key === 'Escape') setQuickNoteMeetingId(null)
                      }}
                      placeholder='כתוב הערה מהירה...'
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: `1px solid ${colors.accentWarning}`,
                        borderRadius: '6px',
                        fontSize: '13px',
                        outline: 'none',
                        color: colors.textPrimary,
                      }}
                      disabled={quickNoteLoading}
                    />
                    <button
                      onClick={() => handleQuickNote(meeting._id)}
                      disabled={quickNoteLoading || !quickNoteText.trim()}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '6px',
                        border: 'none',
                        background: colors.accentWarning,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '12px',
                        cursor: quickNoteText.trim() ? 'pointer' : 'not-allowed',
                        opacity: quickNoteText.trim() ? 1 : 0.5,
                      }}
                    >
                      {quickNoteLoading ? <span className='spinner-border spinner-border-sm'></span> : 'שמור'}
                    </button>
                    <button
                      onClick={() => setQuickNoteMeetingId(null)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: 'none',
                        background: colors.bgTertiary,
                        color: colors.textSecondary,
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      ביטול
                    </button>
                  </div>
                )}

                {/* Onboarding Steps Tracker Sub-row */}
                {status && (
                  <OnboardingStepsRow
                    status={status}
                    accountId={accountId!}
                    onNotesUpdated={(notes) => {
                      setAccountStatuses((prev) => ({
                        ...prev,
                        [accountId!]: { ...prev[accountId!], onboarding_notes: notes },
                      }))
                    }}
                  />
                )}
                </React.Fragment>
              )
            })
            })()}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', gap: '8px'}}>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    border: 'none',
                    background: colors.bgTertiary,
                    color: currentPage === 1 ? colors.textMuted : colors.textSecondary,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <i className='bi bi-chevron-right'></i>
                </button>

                {Array.from({length: Math.min(totalPages, 5)}, (_, i) => {
                  let page: number
                  if (totalPages <= 5) page = i + 1
                  else if (currentPage <= 3) page = i + 1
                  else if (currentPage >= totalPages - 2) page = totalPages - 4 + i
                  else page = currentPage - 2 + i

                  const isActive = currentPage === page
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        border: 'none',
                        background: isActive ? colors.accentPrimary : colors.bgTertiary,
                        color: isActive ? '#fff' : colors.textSecondary,
                        cursor: 'pointer',
                        fontWeight: isActive ? 600 : 400,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {page}
                    </button>
                  )
                })}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    border: 'none',
                    background: colors.bgTertiary,
                    color: currentPage === totalPages ? colors.textMuted : colors.textSecondary,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <i className='bi bi-chevron-left'></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1000,
            overflowY: 'auto',
            padding: '40px 0',
          }}
        >
          <div
            style={{
              background: colors.bgSecondary,
              borderRadius: '12px',
              padding: '28px',
              width: '100%',
              maxWidth: '550px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            {/* Modal Header */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
              <h2 style={{fontSize: '20px', fontWeight: 600, color: colors.textPrimary, margin: 0}}>
                {editingMeeting ? 'עריכת פגישה' : 'פגישה חדשה'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: colors.bgTertiary,
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                }}
              >
                <i className='bi bi-x-lg' style={{fontSize: '16px'}}></i>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Account Select */}
              <div style={{marginBottom: '16px', position: 'relative'}} ref={accountSearchRef}>
                <label style={labelStyle}>
                  חשבון <span style={{color: colors.accentDanger}}>*</span>
                </label>
                {/* Select trigger */}
                <div
                  onClick={() => {
                    if (!showAccountDropdown) {
                      loadRecentAccounts()
                    } else {
                      setShowAccountDropdown(false)
                    }
                  }}
                  style={{
                    ...inputStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: selectedAccount ? 'rgba(54, 153, 255, 0.06)' : colors.bgPrimary,
                    border: selectedAccount ? '1px solid rgba(54, 153, 255, 0.2)' : `1px solid ${colors.borderColor}`,
                  }}
                >
                  {selectedAccount ? (
                    <>
                      <div style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        <span style={{fontWeight: 600, color: colors.textPrimary}}>
                          {selectedAccount.first_name} {selectedAccount.last_name}
                        </span>
                        {accountStatusBadges(selectedAccount).map((b) => (
                          <span
                            key={b.label}
                            style={{
                              padding: '1px 6px', borderRadius: '4px', fontSize: '10px',
                              fontWeight: 600, background: b.bg, color: b.color, marginRight: '6px',
                            }}
                          >{b.label}</span>
                        ))}
                        <span style={{color: colors.textMuted, marginRight: '8px', fontSize: '12px'}}>
                          {selectedAccount.email}
                        </span>
                      </div>
                      <i className='bi bi-x-lg' style={{color: colors.textMuted, fontSize: '12px', flexShrink: 0}}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedAccount(null)
                          setFormData({...formData, account: ''})
                          setAccountSearch('')
                          setAccountResults([])
                          setShowAccountDropdown(false)
                        }}
                      ></i>
                    </>
                  ) : (
                    <>
                      <span style={{color: colors.textMuted}}>בחר חשבון...</span>
                      <i className='bi bi-chevron-down' style={{color: colors.textMuted, fontSize: '12px', transition: 'transform 0.2s', transform: showAccountDropdown ? 'rotate(180deg)' : 'none'}}></i>
                    </>
                  )}
                </div>

                {/* Dropdown */}
                {showAccountDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: colors.bgSecondary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '0 0 6px 6px',
                      zIndex: 10,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      marginTop: '-1px',
                    }}
                  >
                    {/* Search inside dropdown */}
                    <div style={{padding: '8px', borderBottom: `1px solid ${colors.borderColor}`}}>
                      <div style={{position: 'relative'}}>
                        <i className='bi bi-search' style={{position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: colors.textMuted, fontSize: '13px'}}></i>
                        <input
                          type='text'
                          value={accountSearch}
                          onChange={(e) => handleAccountSearch(e.target.value)}
                          autoFocus
                          placeholder='חפש לפי שם או אימייל...'
                          style={{
                            width: '100%',
                            padding: '8px 32px 8px 10px',
                            border: `1px solid ${colors.borderColor}`,
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: colors.textPrimary,
                            background: colors.bgPrimary,
                            outline: 'none',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <label
                        onClick={(e) => e.stopPropagation()}
                        style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '11px', color: colors.textMuted, cursor: 'pointer'}}
                        title="כברירת מחדל מוצגים גם לקוחות שהמנוי שלהם פג. סמן כדי להגיע גם לחשבונות חסומים או לא פעילים."
                      >
                        <input
                          type='checkbox'
                          checked={accountSearchAll}
                          onChange={(e) => toggleAccountSearchAll(e.target.checked)}
                          style={{width: '13px', height: '13px', cursor: 'pointer'}}
                        />
                        <span>כלול גם חשבונות חסומים / לא פעילים</span>
                      </label>
                    </div>
                    {/* Results list */}
                    <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                      {accountResults.length === 0 ? (
                        <div style={{padding: '12px 14px', color: colors.textMuted, fontSize: '13px', textAlign: 'center'}}>
                          לא נמצאו תוצאות
                        </div>
                      ) : (
                        accountResults.map((acc) => (
                          <div
                            key={acc._id}
                            onClick={() => selectAccount(acc)}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: `1px solid ${colors.borderColor}`,
                              fontSize: '13px',
                              color: colors.textPrimary,
                              transition: 'background 0.15s ease',
                              background: formData.account === acc._id ? 'rgba(54, 153, 255, 0.08)' : 'transparent',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgHover)}
                            onMouseLeave={(e) => (e.currentTarget.style.background = formData.account === acc._id ? 'rgba(54, 153, 255, 0.08)' : 'transparent')}
                          >
                            <div style={{fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                              <span>{acc.first_name} {acc.last_name}</span>
                              {accountStatusBadges(acc).map((b) => (
                                <span
                                  key={b.label}
                                  style={{
                                    padding: '1px 6px', borderRadius: '4px', fontSize: '10px',
                                    fontWeight: 600, background: b.bg, color: b.color,
                                  }}
                                >{b.label}</span>
                              ))}
                            </div>
                            <div style={{fontSize: '11px', color: colors.textMuted, display: 'flex', gap: '8px'}}>
                              <span>{acc.email}</span>
                              {acc.phone && <span style={{direction: 'ltr'}}>| {acc.phone}</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Title */}
              <div style={{marginBottom: '16px'}}>
                <label style={labelStyle}>
                  כותרת <span style={{color: colors.accentDanger}}>*</span>
                </label>
                <input
                  type='text'
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  style={inputStyle}
                  placeholder='כותרת הפגישה'
                />
              </div>

              {/* Date + Time */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
                <div>
                  <label style={labelStyle}>
                    תאריך <span style={{color: colors.accentDanger}}>*</span>
                  </label>
                  <input
                    type='date'
                    value={formData.meeting_date}
                    onChange={(e) => setFormData({...formData, meeting_date: e.target.value})}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>שעה</label>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '160px', overflowY: 'auto', border: `1px solid ${colors.bgTertiary}`, borderRadius: '8px', padding: '6px'}}>
                    {TIME_SLOTS.map(slot => {
                      const taken = isSlotTaken(slot, takenSlots)
                      const isPast = formData.meeting_date && isSlotPast(slot, formData.meeting_date)
                      const isDisabled = !!taken || isPast
                      const isSelected = formData.meeting_time === slot
                      return (
                        <button
                          key={slot}
                          type='button'
                          onClick={() => !isDisabled && setFormData({...formData, meeting_time: slot})}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            fontWeight: isSelected ? 700 : 500,
                            border: isSelected ? `2px solid ${colors.accentPrimary}` : '1px solid ' + colors.bgTertiary,
                            borderRadius: '6px',
                            background: taken ? colors.accentDanger + '22' : isPast ? colors.bgTertiary : isSelected ? colors.accentPrimary + '22' : colors.bgSecondary,
                            color: taken ? colors.accentDanger : isPast ? colors.textSecondary : isSelected ? colors.accentPrimary : colors.textPrimary,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            textDecoration: taken ? 'line-through' : 'none',
                            opacity: isDisabled ? 0.6 : 1,
                          }}
                          title={taken ? `תפוס: ${taken.title}${taken.account ? ' - ' + taken.account : ''} (${taken.time})` : isPast ? 'עבר' : ''}
                        >
                          {slot}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Type + Assigned */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
                <div>
                  <label style={labelStyle}>סוג פגישה</label>
                  <select
                    value={formData.meeting_type}
                    onChange={(e) => setFormData({...formData, meeting_type: e.target.value as MeetingType})}
                    style={inputStyle}
                  >
                    {Object.entries(MEETING_TYPE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{position: 'relative'}} ref={adminSearchRef}>
                  <label style={labelStyle}>
                    אדמין אחראי <span style={{color: colors.accentDanger}}>*</span>
                  </label>
                  {selectedAdmin && !showAdminDropdown ? (
                    <div
                      style={{
                        ...inputStyle,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        background: 'rgba(54, 153, 255, 0.06)',
                        border: '1px solid rgba(54, 153, 255, 0.2)',
                      }}
                      onClick={() => {
                        setShowAdminDropdown(true)
                        setAdminSearch('')
                      }}
                    >
                      <span style={{fontWeight: 600, color: colors.textPrimary}}>{selectedAdmin.username}</span>
                      <i className='bi bi-x-lg' style={{color: colors.textMuted, fontSize: '12px'}}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedAdmin(null)
                          setFormData({...formData, assigned_to: ''})
                          setAdminSearch('')
                        }}
                      ></i>
                    </div>
                  ) : (
                    <input
                      type='text'
                      value={adminSearch}
                      onChange={(e) => {
                        setAdminSearch(e.target.value)
                        setShowAdminDropdown(true)
                      }}
                      onFocus={() => setShowAdminDropdown(true)}
                      style={inputStyle}
                      placeholder='חפש אדמין...'
                    />
                  )}
                  {showAdminDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: colors.bgSecondary,
                        border: `1px solid ${colors.borderColor}`,
                        borderRadius: '6px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 10,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      }}
                    >
                      {filteredAdmins.length === 0 ? (
                        <div style={{padding: '10px 14px', color: colors.textMuted, fontSize: '13px'}}>
                          לא נמצאו תוצאות
                        </div>
                      ) : (
                        filteredAdmins.map((admin) => (
                          <div
                            key={admin._id}
                            onClick={() => selectAdmin(admin)}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: `1px solid ${colors.borderColor}`,
                              fontSize: '13px',
                              color: colors.textPrimary,
                              transition: 'background 0.15s ease',
                              background: formData.assigned_to === admin._id ? 'rgba(54, 153, 255, 0.08)' : 'transparent',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgHover)}
                            onMouseLeave={(e) => (e.currentTarget.style.background = formData.assigned_to === admin._id ? 'rgba(54, 153, 255, 0.08)' : 'transparent')}
                          >
                            <div style={{fontWeight: 500}}>{admin.username}</div>
                            <div style={{fontSize: '11px', color: colors.textMuted}}>{admin.email}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Status (edit only) */}
              {editingMeeting && (
                <div style={{marginBottom: '16px'}}>
                  <label style={labelStyle}>סטטוס</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as MeetingStatus})}
                    style={inputStyle}
                  >
                    {Object.entries(MEETING_STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Notes */}
              <div style={{marginBottom: '16px'}}>
                <label style={labelStyle}>הערות (לפני הפגישה)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  style={{...inputStyle, minHeight: '80px', resize: 'vertical' as const}}
                  placeholder='הערות לקראת הפגישה...'
                />
              </div>

              {/* Summary (edit only) */}
              {editingMeeting && (
                <div style={{marginBottom: '16px'}}>
                  <label style={labelStyle}>סיכום (אחרי הפגישה)</label>
                  <textarea
                    value={formData.summary}
                    onChange={(e) => setFormData({...formData, summary: e.target.value})}
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical' as const}}
                    placeholder='סיכום הפגישה...'
                  />
                </div>
              )}

              {/* Internal Notes Thread (edit only) */}
              {editingMeeting && (
                <div style={{marginBottom: '16px'}}>
                  <label style={labelStyle}>הערות פנימיות</label>
                  <div
                    style={{
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '6px',
                      background: colors.bgPrimary,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginBottom: '8px',
                      padding: editingMeeting.internal_notes?.length ? '0' : '12px',
                    }}
                  >
                    {(!editingMeeting.internal_notes || editingMeeting.internal_notes.length === 0) ? (
                      <div style={{color: colors.textMuted, fontSize: '13px', textAlign: 'center'}}>
                        אין הערות עדיין
                      </div>
                    ) : (
                      editingMeeting.internal_notes.map((note: InternalNote) => {
                        const noteAdmin = typeof note.admin === 'string' ? null : (note.admin as AdminUser)
                        const isSystem = note.type === 'system'
                        return (
                          <div
                            key={note._id}
                            style={{
                              padding: '8px 12px',
                              borderBottom: `1px solid ${colors.borderColor}`,
                              background: isSystem ? 'rgba(255, 168, 0, 0.06)' : 'rgba(54, 153, 255, 0.06)',
                              borderRight: `3px solid ${isSystem ? colors.accentWarning : colors.accentPrimary}`,
                            }}
                          >
                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                              <span style={{fontSize: '11px', fontWeight: 600, color: isSystem ? colors.accentWarning : colors.accentPrimary}}>
                                {noteAdmin?.username || 'מערכת'}
                                {isSystem && ' (מערכת)'}
                              </span>
                              <span style={{fontSize: '10px', color: colors.textMuted}}>
                                {moment(note.created_at).format('DD/MM/YY HH:mm')}
                              </span>
                            </div>
                            <div style={{fontSize: '13px', color: colors.textPrimary}}>{note.text}</div>
                          </div>
                        )
                      })
                    )}
                  </div>
                  <div style={{display: 'flex', gap: '8px'}}>
                    <input
                      type='text'
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddNote()
                        }
                      }}
                      placeholder='הוסף הערה...'
                      style={{...inputStyle, flex: 1}}
                    />
                    <button
                      type='button'
                      onClick={handleAddNote}
                      disabled={noteLoading || !newNoteText.trim()}
                      style={{
                        padding: '12px 16px',
                        background: colors.accentPrimary,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: noteLoading || !newNoteText.trim() ? 'not-allowed' : 'pointer',
                        opacity: noteLoading || !newNoteText.trim() ? 0.5 : 1,
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      {noteLoading ? '...' : 'הוסף'}
                    </button>
                  </div>
                </div>
              )}

              {/* WhatsApp Messages (edit only) */}
              {editingMeeting && (
                <div style={{marginBottom: '16px'}}>
                  <label style={labelStyle}>הודעות WhatsApp</label>
                  {(() => {
                    const acc = editingMeeting.account as AccountRef
                    // Lead meetings carry the phone on the meeting itself.
                    const phoneNum = acc?.phone || editingMeeting.contact_phone
                    return phoneNum ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '10px',
                        padding: '6px 10px',
                        background: 'rgba(37, 211, 102, 0.06)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: colors.textSecondary,
                      }}>
                        <i className='bi bi-telephone' style={{color: '#25d366', fontSize: '13px'}}></i>
                        <span>ישלח אל:</span>
                        <span style={{fontWeight: 600, color: colors.textPrimary, direction: 'ltr', unicodeBidi: 'embed'}}>{phoneNum}</span>
                      </div>
                    ) : (
                      <div style={{
                        marginBottom: '10px',
                        padding: '6px 10px',
                        background: 'rgba(246, 78, 96, 0.06)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: colors.accentDanger,
                      }}>
                        <i className='bi bi-exclamation-triangle' style={{marginLeft: '4px'}}></i>
                        אין מספר טלפון לחשבון זה
                      </div>
                    )
                  })()}
                  <div style={{display: 'flex', gap: '8px', marginBottom: '10px'}}>
                    <button
                      type='button'
                      onClick={() => handleSendWhatsApp(editingMeeting, 'scheduled')}
                      disabled={whatsappSending !== null}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        background: 'rgba(37, 211, 102, 0.1)',
                        border: '1px solid rgba(37, 211, 102, 0.25)',
                        borderRadius: '6px',
                        color: '#25d366',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: whatsappSending ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: whatsappSending ? 0.6 : 1,
                      }}
                    >
                      <i className='bi bi-whatsapp'></i>
                      {whatsappSending === 'scheduled' ? 'שולח...' : 'שלח הודעת תיאום'}
                    </button>
                    <button
                      type='button'
                      onClick={() => handleSendWhatsApp(editingMeeting, 'reminder')}
                      disabled={whatsappSending !== null}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        background: 'rgba(255, 168, 0, 0.1)',
                        border: '1px solid rgba(255, 168, 0, 0.25)',
                        borderRadius: '6px',
                        color: colors.accentWarning,
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: whatsappSending ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: whatsappSending ? 0.6 : 1,
                      }}
                    >
                      <i className='bi bi-bell'></i>
                      {whatsappSending === 'reminder' ? 'שולח...' : 'שלח תזכורת'}
                    </button>
                  </div>

                  {/* Message log */}
                  {editingMeeting.whatsapp_messages && editingMeeting.whatsapp_messages.length > 0 && (
                    <div
                      style={{
                        border: `1px solid ${colors.borderColor}`,
                        borderRadius: '6px',
                        background: colors.bgPrimary,
                        maxHeight: '150px',
                        overflowY: 'auto',
                      }}
                    >
                      {editingMeeting.whatsapp_messages.map((msg: WhatsAppMessage, idx: number) => (
                        <div
                          key={msg._id || idx}
                          style={{
                            padding: '8px 12px',
                            borderBottom: `1px solid ${colors.borderColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                          }}
                        >
                          <i
                            className={`bi ${msg.status === 'sent' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}
                            style={{
                              color: msg.status === 'sent' ? '#25d366' : colors.accentDanger,
                              fontSize: '14px',
                              flexShrink: 0,
                            }}
                          ></i>
                          <div style={{flex: 1, minWidth: 0}}>
                            <span style={{fontWeight: 600, color: colors.textPrimary}}>
                              {msg.type === 'scheduled' ? 'הודעת תיאום' : 'תזכורת'}
                            </span>
                            <span style={{color: colors.textMuted, marginRight: '8px'}}>
                              → {msg.phone_number}
                            </span>
                            {msg.status === 'failed' && msg.error && (
                              <span style={{color: colors.accentDanger, marginRight: '8px'}}>({msg.error})</span>
                            )}
                          </div>
                          <span style={{color: colors.textMuted, fontSize: '11px', flexShrink: 0}}>
                            {moment(msg.sent_at).format('DD/MM HH:mm')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{marginTop: '8px', textAlign: 'left'}}>
                    <button
                      type='button'
                      onClick={() => {
                        loadTemplates()
                        setShowTemplateModal(true)
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.textMuted,
                        fontSize: '11px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: 0,
                      }}
                    >
                      ערוך תבניות הודעה
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                <button
                  type='button'
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '12px 24px',
                    background: colors.bgTertiary,
                    border: 'none',
                    borderRadius: '6px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  ביטול
                </button>
                <button
                  type='submit'
                  disabled={formLoading}
                  style={{
                    padding: '12px 24px',
                    background: colors.accentPrimary,
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                    opacity: formLoading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {formLoading ? (
                    <>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite',
                        }}
                      ></div>
                      שומר...
                    </>
                  ) : editingMeeting ? (
                    <>
                      <i className='bi bi-check-lg'></i>
                      עדכן
                    </>
                  ) : (
                    <>
                      <i className='bi bi-plus-lg'></i>
                      צור פגישה
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleTarget && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
          }}
        >
          <div
            style={{
              background: colors.bgSecondary,
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '380px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: '0 0 20px 0'}}>
              דחיית פגישה
            </h3>
            <p style={{fontSize: '13px', color: colors.textSecondary, marginBottom: '16px'}}>
              {getAccountName(rescheduleTarget)} — {rescheduleTarget.title}
            </p>
            <div style={{marginBottom: '16px'}}>
              <label style={labelStyle}>תאריך חדש</label>
              <input
                type='date'
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{marginBottom: '20px'}}>
              <label style={labelStyle}>שעה</label>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '160px', overflowY: 'auto', border: `1px solid ${colors.bgTertiary}`, borderRadius: '8px', padding: '6px'}}>
                {TIME_SLOTS.map(slot => {
                  const taken = isSlotTaken(slot, rescheduleTakenSlots)
                  const isPast = rescheduleDate && isSlotPast(slot, rescheduleDate)
                  const isDisabled = !!taken || isPast
                  const isSelected = rescheduleTime === slot
                  return (
                    <button
                      key={slot}
                      type='button'
                      onClick={() => !isDisabled && setRescheduleTime(slot)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        fontWeight: isSelected ? 700 : 500,
                        border: isSelected ? `2px solid ${colors.accentPrimary}` : '1px solid ' + colors.bgTertiary,
                        borderRadius: '6px',
                        background: taken ? colors.accentDanger + '22' : isPast ? colors.bgTertiary : isSelected ? colors.accentPrimary + '22' : colors.bgSecondary,
                        color: taken ? colors.accentDanger : isPast ? colors.textSecondary : isSelected ? colors.accentPrimary : colors.textPrimary,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        textDecoration: taken ? 'line-through' : 'none',
                        opacity: isDisabled ? 0.6 : 1,
                      }}
                      title={taken ? `תפוס: ${taken.title}${taken.account ? ' - ' + taken.account : ''} (${taken.time})` : isPast ? 'עבר' : ''}
                    >
                      {slot}
                    </button>
                  )
                })}
              </div>
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button
                type='button'
                onClick={() => {
                  setShowRescheduleModal(false)
                  setRescheduleTarget(null)
                }}
                style={{
                  padding: '10px 20px',
                  background: colors.bgTertiary,
                  border: 'none',
                  borderRadius: '6px',
                  color: colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                type='button'
                onClick={handleReschedule}
                disabled={rescheduleLoading || !rescheduleDate}
                style={{
                  padding: '10px 20px',
                  background: colors.accentWarning,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: rescheduleLoading || !rescheduleDate ? 'not-allowed' : 'pointer',
                  opacity: rescheduleLoading ? 0.7 : 1,
                }}
              >
                {rescheduleLoading ? 'שומר...' : 'דחה פגישה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1002,
            overflowY: 'auto',
            padding: '40px 0',
          }}
        >
          <div
            style={{
              background: colors.bgSecondary,
              borderRadius: '12px',
              padding: '28px',
              width: '100%',
              maxWidth: '550px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              flexShrink: 0,
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3 style={{fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0}}>
                תבניות הודעות WhatsApp
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                style={{
                  background: colors.bgTertiary,
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                }}
              >
                <i className='bi bi-x-lg' style={{fontSize: '16px'}}></i>
              </button>
            </div>

            <div style={{marginBottom: '8px', padding: '8px 12px', background: 'rgba(54, 153, 255, 0.08)', borderRadius: '6px', fontSize: '12px', color: colors.textSecondary}}>
              משתנים זמינים: <code>{'{name}'}</code> <code>{'{date}'}</code> <code>{'{time}'}</code> <code>{'{title}'}</code>
            </div>

            <div style={{marginBottom: '16px'}}>
              <label style={labelStyle}>הודעת תיאום (נשלחת אוטומטית ביצירת פגישה)</label>
              <textarea
                value={templates.scheduled}
                onChange={(e) => setTemplates({...templates, scheduled: e.target.value})}
                style={{...inputStyle, minHeight: '140px', resize: 'vertical' as const, fontFamily: 'inherit', direction: 'rtl'}}
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <label style={labelStyle}>הודעת תזכורת (נשלחת ידנית / 5 דקות לפני)</label>
              <textarea
                value={templates.reminder}
                onChange={(e) => setTemplates({...templates, reminder: e.target.value})}
                style={{...inputStyle, minHeight: '140px', resize: 'vertical' as const, fontFamily: 'inherit', direction: 'rtl'}}
              />
            </div>

            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button
                type='button'
                onClick={() => setShowTemplateModal(false)}
                style={{
                  padding: '10px 20px',
                  background: colors.bgTertiary,
                  border: 'none',
                  borderRadius: '6px',
                  color: colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                type='button'
                onClick={handleSaveTemplates}
                disabled={templateLoading}
                style={{
                  padding: '10px 20px',
                  background: '#25d366',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: templateLoading ? 'not-allowed' : 'pointer',
                  opacity: templateLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <i className='bi bi-whatsapp'></i>
                {templateLoading ? 'שומר...' : 'שמור תבניות'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        select option {
          background: ${colors.bgSecondary};
          color: ${colors.textPrimary};
        }
      `}</style>
    </div>
  )
}

export default MeetingsPage
